/**
 * 23 Oct 2026 (D-1023-READING) — the Reading & Services close-out: the office's one short form. Did they meet
 * (yes / no / cancelled), how long, notes. No five questions, no completion form, nothing to the volunteer but
 * the thank-you the server queues when they met.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { ChoiceField, TextArea, TextField } from '@/features/people/fields';
import { READING_MET } from '@shared/volunteers';
import { postJson, useVolunteerRefresh } from './api';

export interface ReadingVisitRef {
  appointmentId: string;
  who: string;
  name: string;
  day: string;
  duration: number;
}

type Result = { met: 'yes' | 'no' | 'cancelled'; thanks: 'queued' | 'no-email' | 'opted-out' | 'none' };

export function ReadingCloseoutSheet({ visit, onClose, activity }: { visit: ReadingVisitRef; onClose: () => void; activity?: boolean }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const uid = useId();
  const [met, setMet] = useState<'yes' | 'no' | 'cancelled' | ''>('');
  const [minutes, setMinutes] = useState(String(visit.duration || 60));
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!met) {
      const m = t('vol.form.fixErrors');
      setErr(m);
      announce(m, 'assertive');
      return;
    }
    const n = Number(minutes);
    if (met === 'yes' && !(Number.isFinite(n) && n > 0)) {
      const m = t('reading1023.needMinutes');
      setErr(m);
      announce(m, 'assertive');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const r = await postJson<Result>(`/api/volunteers/visits/${encodeURIComponent(visit.appointmentId)}`, { what: 'office-closeout', closeout: { met, ...(met === 'yes' ? { minutes: Math.round(n) } : {}), notes } });
      const m = r.thanks === 'queued' ? t('reading1023.savedThanks', { name: visit.name }) : r.thanks === 'no-email' ? t('reading1023.savedNoEmail', { name: visit.name }) : r.thanks === 'opted-out' ? t('reading1023.savedOptedOut', { name: visit.name }) : t('reading1023.saved');
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setErr(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SheetContent
      title={activity ? t('reading1023.activityTitle') : t('reading1023.title')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('reading1023.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void save()} data-testid="reading-closeout-save">
            {t('reading1023.save')}
          </Button>
        </>
      }
    >
      <div data-testid="reading-closeout-form">
        <p className="help">{t('reading1023.intro', { who: visit.who, day: visit.day })}</p>
        <ChoiceField
          id={`${uid}-met`}
          label={t('reading1023.metLegend')}
          required
          value={met}
          onChange={setMet}
          options={READING_MET.map((v) => ({ value: v, label: t(`reading1023.${v}`) }))}
        />
        {met === 'yes' ? <TextField id={`${uid}-min`} label={t('reading1023.minutes')} required value={minutes} onChange={setMinutes} inputMode="numeric" help={t('reading1023.minutesHelp')} /> : null}
        <TextArea id={`${uid}-notes`} label={t('reading1023.notes')} value={notes} onChange={setNotes} />
        {err ? (
          <p role="alert" className="help" data-testid="reading-closeout-err">
            {err}
          </p>
        ) : null}
      </div>
    </SheetContent>
  );
}
